import * as React from 'react';
import { css, cx } from '@emotion/css';
import { theme } from '@branchjs/neo-components';
import StatusLabel from '../../../../../common/components/core/StatusLabel';
import Button from '../../../../../common/components/core/Button';
import DiscoAdsAppIconWrapper from '../DiscoAdsAppIconWrapper';
import Icons from '../../../../../../styles/Icons';
import { truncate } from '../../../../../../js/main/support/utils';
import { RE_ENGAGEMENT_CAMPAIGN } from '../../discoAdsConstants';
import type { DiscoAdPreviewerProps } from './DiscoAdPreviewer';
const LABEL_TYPE = 'default';
const LABEL_TEXT = 'Ad';
const INSTALL_BTN_TEXT = 'GET';
const OPEN_BTN_TEXT = 'OPEN';
const TRUNCATED_DESCRIPTION_THRESHOLD = 35;
const styles = {
  adContainer: css({
    display: 'flex',
    flex: 1,
    justifyContent: 'space-around',
    alignItems: 'center',
    height: '60px',
    backgroundColor: theme.colors.primary.contrastWhite,
    marginTop: '40px',
    padding: '8px 7px 8px 7px',
  }),
  imagePlaceholderContainer: css({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '40px',
    width: '40px',
    marginRight: '3px',
    backgroundColor: theme.colors.primary.contrastWhite,
    boxSizing: 'border-box',
    border: `1px solid ${theme.colors.gray[200]}`,
    borderRadius: '5px',
  }),
  imagePlaceholderIcon: css({
    fontFamily: 'Material Icons',
    fontSize: 30,
    color: theme.colors.primary.main,
    fontStyle: 'normal',
  }),
  appIconContainer: css({
    height: '40px',
    width: '40px',
    marginRight: '3px',
  }),
  headlineAndDescriptionContainer: css({
    width: '169px',
  }),
  headlineText: css({
    fontSize: theme.typography.subtitle2.fontSize,
    fontWeight: theme.typography.subtitle2.fontWeight,
    color: theme.colors.gray[800],
  }),
  labelAndDescriptionContainer: css({
    width: '210px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  }),
  statusLabel: css({
    borderRadius: '5px',
    paddingTop: '1px',
  }),
  statusLabelText: css({
    fontWeight: 'bold',
    fontSize: '10px',
    padding: '1px 5px !important',
    borderRadius: '5px',
  }),
  descriptionText: css({
    fontSize: theme.typography.subtitle2.fontSize,
    fontWeight: 400,
    opacity: 0.85,
    marginLeft: '1px',
    paddingTop: '2px',
  }),
  button: css({
    alignSelf: 'baseline',
    fontWeight: 'bold',
    color: theme.colors.primary.main,
    borderColor: theme.colors.primary.main,
    borderRadius: '3px',
    padding: '2px 10px',
    cursor: 'default',
    '&:hover,&:focus': css({
      color: theme.colors.primary.main,
      borderColor: theme.colors.primary.main,
      backgroundColor: theme.colors.primary.contrastWhite,
    }),
  }),
};

class DiscoAdPreview extends React.PureComponent<DiscoAdPreviewerProps> {
  renderThumbnail = (): React.ReactNode => {
    const { creativeImageUrl } = this.props;

    if (creativeImageUrl) {
      const customAppIconStyles = {
        iconContainer: styles.appIconContainer,
      };
      return <DiscoAdsAppIconWrapper url={creativeImageUrl} title="ad_preview" styles={customAppIconStyles} />;
    }

    return (
      <div className={styles.imagePlaceholderContainer}>
        <i className={styles.imagePlaceholderIcon}>{Icons.IMAGE}</i>
      </div>
    );
  };

  renderDescriptionText = (): string => {
    const { description } = this.props;

    if (description.length >= TRUNCATED_DESCRIPTION_THRESHOLD) {
      return truncate(description, TRUNCATED_DESCRIPTION_THRESHOLD);
    }

    return description;
  };

  _getButtonText = (): string => {
    if (this.props.campaignObjective === RE_ENGAGEMENT_CAMPAIGN) {
      return OPEN_BTN_TEXT;
    }

    return INSTALL_BTN_TEXT;
  };

  render() {
    const { headline } = this.props;
    const {
      adContainer,
      headlineAndDescriptionContainer,
      headlineText,
      statusLabel,
      statusLabelText,
      labelAndDescriptionContainer,
      descriptionText,
      button,
    } = styles;

    const buttonText = this._getButtonText();

    const buttonStyling =
      buttonText === OPEN_BTN_TEXT
        ? cx(
            button,
            css({
              padding: '2px 8px',
            })
          )
        : button;
    return (
      <div className={adContainer}>
        {this.renderThumbnail()}
        <div className={headlineAndDescriptionContainer}>
          <div className={headlineText}>{headline}</div>
          <div className={labelAndDescriptionContainer}>
            <StatusLabel type={LABEL_TYPE} text={LABEL_TEXT} className={statusLabel} textClassName={statusLabelText} />
            <div className={descriptionText}>{this.renderDescriptionText()}</div>
          </div>
        </div>
        <Button testid="dac-ad-preview" size="small" type="secondary" className={buttonStyling}>
          {buttonText}
        </Button>
      </div>
    );
  }
}

export default DiscoAdPreview;
